<script>
  import route from "@helpers/route"
  import { toHuman } from "@helpers/formatters/currency"
  import Link from "@components/Link.svelte"
  import EmbeddedProductCardImages from "@components/EmbeddedProductCardImages.svelte"
  import AffirmPromotionalMessaging, { AffirmPageType } from "@components/AffirmPromotionalMessaging.svelte"
  import { merchant } from "@store"
  import ProductAvailability from "@components/ProductAvailability.svelte"
  import { getUnitPriceSuffix } from "@helpers/models/product"

  export let product
  export let collection

  export let shouldUsePostMessageNavigation = false

  const url = route("shop.collections.products.show", [collection, collection.slug, product, product.slug])

  $: isSampleRequest = product.selection.is_sample_request && product.is_sampleable
  $: unitPriceInCents = isSampleRequest ? product.sample_price_in_cents : product.retail_price_with_shipping_in_cents
  $: unitPriceSuffix = isSampleRequest ? "/ea" : getUnitPriceSuffix(product)

  /** @type {boolean} */
  let hovering = false

  const requestPostMessageNavigation = () => {
    if (window === window.parent) {
      // Not in an iframe, probably previewing. Navigate normally
      window.location.href = url
    } else {
      // In an iframe, ask the parent to navigate.
      window.parent.postMessage(
        {
          embedId: `onsidedoor-embed-${collection.id}`,
          command: "navigate",
          href: url,
        },
        "*",
      )
    }
  }
</script>

<article
  class="c-product-card c-product-card--embed relative flex flex-col grow"
  on:mouseenter={() => (hovering = true)}
  on:mouseleave={() => (hovering = false)}
>
  <div class="c-product-card__photo [ rounded border relative aspect-ratio-square ]">
    {#if shouldUsePostMessageNavigation}
      <a href={url} on:click|preventDefault={requestPostMessageNavigation} class="h-full w-full block group">
        <EmbeddedProductCardImages {product} {hovering} />
      </a>
    {:else}
      <Link href={url} title="View {product.name}" class="h-full w-full block group" external>
        <EmbeddedProductCardImages {product} {hovering} />
      </Link>
    {/if}
  </div>

  <div class="c-product-card__details mt-4 flex flex-col grow">
    {#if collection.settings.shows_vendor_name}
      <p class="c-product-card__vendor [ text-sm font-functional ]">
        {product.vendor.name}
      </p>
    {/if}

    {#if shouldUsePostMessageNavigation}
      <a href={url} on:click|preventDefault={requestPostMessageNavigation}>
        <h2 class="c-product-card__name [ text-lg font-bold leading-tight line-clamp-2 ]">
          {product.name}
        </h2>
      </a>
    {:else}
      <Link type="stroke" href={url} external>
        <h2 class="c-product-card__name [ text-lg font-bold leading-tight line-clamp-2 ]">
          {product.name}
        </h2>
      </Link>
    {/if}

    <div class="mt-2">
      <span class="c-product-card__price [ block text-2xl font-semibold tracking-tighter mt]">
        {toHuman(unitPriceInCents * 0.01)}{unitPriceSuffix}
      </span>
    </div>

    <div class="space-y-3 pt-2 mt-auto">
      {#if $merchant.show_affirm_product_promotional_messaging && !collection.settings.hide_affirm_product_promotional_messaging}
        <AffirmPromotionalMessaging
          class="font-sans text-sm"
          amountInCents={unitPriceInCents}
          pageType={AffirmPageType.Search}
        />
      {/if}

      <ProductAvailability {product} {collection} sample={isSampleRequest} disableInquireButton={true} />
    </div>
  </div>
</article>
